import type { PageElement, StowedElement } from '@/api/types/stowed-element'
import { KIND_TO_FIELD } from '@/api/types/stowed-element'
import { elementUsesVisibleEditorRect } from '@/lib/elements/read-page-element-editor-rect'

/**
 * A rotated template element with its `visible*` box set to its own frame (`x/y/width/height`),
 * the way Larry writes one.
 *
 * Larry reads a rotated element's frame from `base.visible*` (`readPageElementEditorRectFromBase`),
 * treating it as the element's centre and size. In an NSD decomposed from the legacy app that is
 * not what `visible*` holds: `VX`/`VY` are small offsets of the rotated bounding box from the
 * element's centre (`-0.0144`, not `354.5`) and `VW`/`VH` are that bounding box (196, for a
 * 161.8pt frame). Read as a centre, every rotated element in a template lands at the spread's
 * centre, drawn at its bounding-box size.
 *
 * The frame itself (`x`, `y`, `width`, `height`) is the real one in either source, and the rotation
 * is applied about its centre, so mirroring it into `visible*` makes the template read like
 * something Larry saved. Unrotated elements are left alone: they are read from the frame already.
 * Scoped to templates on purpose; it leaves how every other load reads `visible*` as it was.
 */
export function normalizeTemplateRotatedFrame(element: StowedElement): StowedElement {
  const field = KIND_TO_FIELD[element.kindId]
  const kindData = field ? (element[field] as { base?: PageElement } | undefined) : undefined
  const base = kindData?.base

  if (!field || !kindData || !base) return element

  const rotation = Number(base.rotation ?? 0)

  if (!Number.isFinite(rotation) || !elementUsesVisibleEditorRect(rotation)) return element

  return {
    ...element,
    [field]: {
      ...kindData,
      base: {
        ...base,
        visibleX: base.x,
        visibleY: base.y,
        visibleWidth: base.width,
        visibleHeight: base.height,
      },
    },
  }
}
